/**
 * @fileoverview Modal for ceasing (archiving) a budget into read-only mode.
 * Allows owner to select a cease date and confirms the action with explanation.
 */

import { useState, useEffect } from 'react';
import ButtonSpinner from '../Loading/ButtonSpinner';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './BudgetManagement.module.css';

/**
 * CeaseBudgetModal Component
 *
 * Modal overlay for ceasing a budget with date selection and feature explanation.
 *
 * Props:
 * - isOpen (boolean): Whether modal is visible
 * - budgetKey (string): Key of budget being ceased
 * - budgetName (string): Name of budget being ceased
 * - onClose (function): Called when modal should close
 * - onConfirm (function): Called with ceaseDate when confirm is clicked
 * - error (string): Error message to display
 */
function CeaseBudgetModal({ isOpen, budgetKey, budgetName, onClose, onConfirm, error }) {
    const [ceaseDate, setCeaseDate] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useModalScrollLock(isOpen);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setIsSaving(false);
            // Default to today's date
            const today = new Date().toISOString().split('T')[0];
            setCeaseDate(today);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await onConfirm(budgetKey, ceaseDate);
            // Parent will close modal on success
        } catch (error) {
            // Re-enable button on error
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <button
                    onClick={onClose}
                    disabled={isSaving}
                    className={styles.closeButton}
                    aria-label="Close"
                >
                    ×
                </button>

                <h2 className={styles.modalTitle}>Cease Budget</h2>

                <form onSubmit={handleSubmit}>
                    <div className={styles.formGroup}>
                        <p className={styles.budgetNameDisplay}>
                            <strong>{budgetName}</strong>
                        </p>
                        <p className={styles.modalDescription}>
                            Ceasing a budget archives it into read-only mode. This prevents all modifications while preserving data for viewing and reporting.
                        </p>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="ceaseDate" className={styles.label}>
                            Cease Date
                        </label>
                        <input
                            type="date"
                            id="ceaseDate"
                            value={ceaseDate}
                            onChange={(e) => setCeaseDate(e.target.value)}
                            className={styles.input}
                            required
                            autoFocus
                        />
                        <div className={styles.helperText}>
                            Month navigation will be limited to months on or before this date
                        </div>
                    </div>

                    <div className={styles.featureList}>
                        <p className={styles.featureListTitle}>When ceased, this budget will be:</p>
                        <ul className={styles.featureListItems}>
                            <li>Read-only for all users (including you as owner)</li>
                            <li>Unable to add, edit, or delete transactions</li>
                            <li>Unable to add, edit, or delete categories</li>
                            <li>Unable to modify budget templates</li>
                            <li>Unable to update bank balances</li>
                            <li>Still viewable for reports and searches</li>
                        </ul>
                    </div>

                    <div className={styles.reversibleNote}>
                        <strong>This action is reversible.</strong> You can restore the budget at any time from this page.
                    </div>

                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSaving}
                            className={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving || !ceaseDate}
                            className={styles.ceaseButton}
                        >
                            {isSaving && <ButtonSpinner />}
                            {isSaving ? 'Ceasing...' : 'Cease Budget'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CeaseBudgetModal;
